import {
  LayoutDashboard,
  Users,
  MapPin,
  CalendarDays,
  ClipboardCheck,
  Activity,
  BarChart3,
  FileText,
  Settings,
  Store,
  Route,
  Target,
  LogOut,
  UserCog,
  Camera,
  Trophy,
  DollarSign,
  MessageCircle,
  ClipboardList,
  Star,
  Calendar,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

type AppRole = Database["public"]["Enums"]["app_role"];

interface MenuItem {
  title: string;
  url: string;
  icon: any;
  roles?: AppRole[]; // if undefined, all roles can see it
}

const mainItems: MenuItem[] = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard, roles: ["admin", "gestor", "supervisor", "visualizacao", "promotor"] },
  { title: "Monitoramento", url: "/monitoramento", icon: Activity, roles: ["admin", "gestor", "supervisor"] },
  { title: "Mensagens", url: "/chat", icon: MessageCircle, roles: ["admin", "gestor", "supervisor", "promotor"] },
];

const cadastroItems: MenuItem[] = [
  { title: "Equipe de Campo", url: "/equipe", icon: Users, roles: ["admin", "gestor", "supervisor"] },
  { title: "PDVs / Lojas", url: "/pdvs", icon: Store, roles: ["admin", "gestor"] },
  { title: "Regiões / Rotas", url: "/regioes", icon: Route, roles: ["admin", "gestor"] },
  { title: "Tarefas", url: "/tarefas", icon: ClipboardCheck, roles: ["admin", "gestor"] },
  { title: "Campanhas", url: "/campanhas", icon: Target, roles: ["admin", "gestor"] },
  { title: "Preços", url: "/precos", icon: DollarSign, roles: ["admin", "gestor", "supervisor", "promotor"] },
  { title: "Pesquisas", url: "/pesquisas", icon: ClipboardList, roles: ["admin", "gestor"] },
];

const operacaoItems: MenuItem[] = [
  { title: "Planejamento", url: "/planejamento", icon: CalendarDays, roles: ["admin", "gestor", "supervisor"] },
  { title: "Execução", url: "/execucao", icon: MapPin, roles: ["admin", "gestor", "supervisor", "promotor"] },
  { title: "Auditoria", url: "/auditoria", icon: ClipboardCheck, roles: ["admin", "gestor", "supervisor"] },
  { title: "Loja Perfeita", url: "/loja-perfeita", icon: Star, roles: ["admin", "gestor", "supervisor", "visualizacao"] },
  { title: "Validade", url: "/validade", icon: Calendar, roles: ["admin", "gestor", "supervisor", "visualizacao"] },
];

const relatorioItems: MenuItem[] = [
  { title: "Indicadores", url: "/indicadores", icon: BarChart3, roles: ["admin", "gestor", "supervisor", "visualizacao"] },
  { title: "Relatórios", url: "/relatorios", icon: FileText, roles: ["admin", "gestor", "supervisor", "visualizacao"] },
  { title: "Evidências", url: "/evidencias", icon: Camera, roles: ["admin", "gestor", "supervisor", "visualizacao"] },
  { title: "Desempenho", url: "/desempenho", icon: Trophy, roles: ["admin", "gestor", "supervisor", "visualizacao"] },
];

const adminItems: MenuItem[] = [
  { title: "Usuários", url: "/admin/usuarios", icon: UserCog, roles: ["admin"] },
];

export function AppSidebar() {
  const { state, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const { signOut, profile, roles } = useAuth();
  const isPromotor = roles.includes("promotor") && !roles.includes("admin") && !roles.includes("gestor") && !roles.includes("supervisor");

  const handleNavClick = () => {
    setOpenMobile(false);
  };

  const isActive = (path: string) => currentPath === path;

  const filterByRole = (items: MenuItem[]) => {
    if (roles.length === 0) return items; // loading, show all
    return items.filter((item) => !item.roles || item.roles.some((r) => roles.includes(r)));
  };

  const filteredMain = filterByRole(mainItems);
  const filteredCadastro = filterByRole(cadastroItems);
  const filteredOperacao = filterByRole(operacaoItems);
  const filteredRelatorio = filterByRole(relatorioItems);
  const filteredAdmin = filterByRole(adminItems);

  const renderGroup = (label: string, items: MenuItem[]) => {
    if (items.length === 0) return null;
    return (
      <SidebarGroup>
        <SidebarGroupLabel>{label}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title} onClick={handleNavClick}>
                  <NavLink to={item.url} end={item.url === "/"}>
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-2">
          <img src="/icons/logo-frico.webp" alt="Fricó Alimentos" className="h-8 w-8 shrink-0 rounded-lg object-contain" />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-sm font-bold text-sidebar-foreground">Fricó Alimentos</span>
              <span className="text-[10px] text-sidebar-foreground/60">Gestão de Campo</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="scrollbar-thin">
        {isPromotor ? (
          renderGroup("Principal", [...filteredMain, ...filteredCadastro, ...filteredOperacao])
        ) : (
          <>
            {renderGroup("Principal", filteredMain)}
            {renderGroup("Cadastros", filteredCadastro)}
            {renderGroup("Operação", filteredOperacao)}
            {renderGroup("Análise", filteredRelatorio)}
            {renderGroup("Administração", filteredAdmin)}
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/configuracoes")} tooltip="Configurações" onClick={handleNavClick}>
              <NavLink to="/configuracoes">
                <Settings className="h-4 w-4" />
                <span>Configurações</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip="Sair" onClick={() => {
              if (window.confirm("Tem certeza que deseja sair?")) signOut();
            }}>
              <LogOut className="h-4 w-4" />
              <span>Sair</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
